// Phase R — honest replacement for the v1 «ОШИБКИ 785» counter.
//
// Renders a compact horizontal strip with one chip per R category.
// «не вычислено» (service_failed) is the only one that uses the
// accent colour — that's the real-failure bucket. Everything else
// reads neutral so the operator sees at a glance that catalogue
// limits / infrastructure gaps aren't system bugs.
//
// Per spec §B.1 the chip layout is «функционально и читаемо», not
// designer-final. Each chip is a button that filters the Feed to
// jobs in that category — wired via the `onCategoryClick` prop.
//
// Categories with zero count are STILL rendered (greyed) so the UI
// shape stays stable as counts move; an operator deciphering the
// strip can confirm «yes, no_reference is genuinely zero today»
// rather than wondering why the chip vanished.

import type { FailureBreakdownResponse, FailureCategoryR } from '../api/types';
import {
  FAILURE_CATEGORY_R_IS_REAL_FAILURE,
  FAILURE_CATEGORY_R_LABEL,
} from '../api/types';
import { cn } from '../lib/classnames';
import './FailureBreakdownStrip.css';

/** Ordered display sequence — service_failed first (most
 *  operator-relevant), then catalogue-reality categories in
 *  decreasing typical count (file_unavailable >> not_applicable
 *  on the prod snapshot), then no_reference (DONE-side), then
 *  other (the tail). Mirrors what the prod diagnostic surfaced. */
const DISPLAY_ORDER: FailureCategoryR[] = [
  'service_failed',
  'file_unavailable',
  'not_applicable',
  'no_reference',
  'other',
];

const COUNT_FIELD: Record<FailureCategoryR, keyof FailureBreakdownResponse> = {
  service_failed: 'service_failed',
  file_unavailable: 'file_unavailable',
  not_applicable: 'not_applicable',
  no_reference: 'no_reference',
  other: 'other',
};

export function FailureBreakdownStrip({
  breakdown,
  onCategoryClick,
}: {
  breakdown: FailureBreakdownResponse | undefined;
  onCategoryClick?: (category: FailureCategoryR) => void;
}) {
  return (
    <div
      className="failure-breakdown-strip"
      data-testid="failure-breakdown-strip"
    >
      {DISPLAY_ORDER.map((cat) => {
        const count = breakdown ? (breakdown[COUNT_FIELD[cat]] as number) : 0;
        const isReal = FAILURE_CATEGORY_R_IS_REAL_FAILURE[cat];
        // C4a — a chip is interactive only when a handler is wired AND the
        // category has jobs. A zero-count chip stays rendered (greyed) for a
        // stable UI shape but is disabled, so a click can never open an
        // empty pre-filtered Feed. Read-only usages (no handler) stay fully
        // disabled regardless of count.
        const clickable = onCategoryClick != null;
        const cls = cn(
          'fbs-chip',
          isReal && 'fbs-chip--accent',
          !isReal && 'fbs-chip--muted',
          count === 0 && 'fbs-chip--zero',
        );
        return (
          <button
            key={cat}
            type="button"
            className={cls}
            disabled={!clickable || count === 0}
            onClick={() => onCategoryClick?.(cat)}
            data-testid={`fbs-chip-${cat}`}
            aria-label={`фильтр: ${FAILURE_CATEGORY_R_LABEL[cat]} (${count})`}
          >
            <span className="fbs-num">{breakdown == null ? '—' : count}</span>
            <span className="fbs-label">{FAILURE_CATEGORY_R_LABEL[cat]}</span>
          </button>
        );
      })}
    </div>
  );
}
